import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TableHead,
	TableRow,
	Typography,
	styled,
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import { FC } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { AddOn, SelectedAddOn } from '@acorn-portal/shared/src/services/renewalsApi';
import { formatCurrency } from '@acorn-portal/shared/src/utils';
import { Product } from '@acorn-portal/shared/src/services/interface/Shared';
import {
	ACORN_BREAKDOWN_COVER_BOOKLET,
	ACORN_BREAKDOWN_COVER_IPID,
	CARROT_BREAKDOWN_COVER_BOOKLET,
	CARROT_BREAKDOWN_COVER_IPID,
	FLAG_BREAKDOWN_COVER_BOOKLET,
	FLAG_BREAKDOWN_COVER_IPID,
	OPTIONAL_EXTRAS_MONTHLY_COST_DISCLAIMER,
	STREETCOVER_BREAKDOWN_COVER_BOOKLET,
	STREETCOVER_BREAKDOWN_COVER_IPID,
} from '@acorn-portal/shared/src/utils/constants';
import { getKeyValue } from '@acorn-portal/shared/src/utils/get-key-value';

interface IRowData {
	name: string;
	s1: boolean;
	s2: boolean;
}

const HyperLink = styled('a')(({ theme }) => ({
	color: theme.palette.primary.main,
	'&:hover': {
		color: theme.palette.primary.light,
	},
	'&:visited': {
		color: theme.palette.primary.dark,
		'&:hover': {
			color: theme.palette.primary.light,
		},
	},
}));

const BREAKDOWNCOVER: IRowData[] = [
	{
		name: '24/7 Roadside assistance',
		s1: true,
		s2: true,
	},
	{
		name: 'No call out charge',
		s1: true,
		s2: true,
	},
	{
		name: 'Misfueling cover',
		s1: true,
		s2: true,
	},
	{
		name: 'Cover at home',
		s1: true,
		s2: true,
	},
	{
		name: 'Long distance recovery',
		s1: true,
		s2: true,
	},
	{
		name: 'Onward transport options',
		s1: true,
		s2: true,
	},
	{
		name: 'European cover',
		s1: false,
		s2: true,
	},
];

interface IColumns {
	title: string;
	field: string;
	align?: 'left' | 'center' | 'right' | 'justify' | 'inherit';
}

const COLUMNS: IColumns[] = [
	{
		title: 'Breakdown Service',
		field: 'name',
	},
	{
		title: 'Home call & Breakdown Service',
		field: 's1',
		align: 'center',
	},
	{
		title: 'European Plus',
		field: 's2',
		align: 'center',
	},
];

const GenerateValue = (value: string | boolean) => {
	if (typeof value === 'boolean') {
		return value ? <DoneIcon fontSize='large' color='success' /> : <CloseIcon fontSize='large' color='error' />;
	}
	return value;
};

type INavLink = {
	title: string;
	href: string;
};

interface IBreakdownCoverLinks {
	pc: INavLink;
	cv: INavLink;
	tx: INavLink;
	carrot: INavLink;
	txFlag: INavLink;
	streetcover: INavLink;
}

const BreakdownCoverLinks: IBreakdownCoverLinks[] = [
	{
		txFlag: {
			title: 'Breakdown Cover Booklet',
			href: FLAG_BREAKDOWN_COVER_BOOKLET,
		},
		pc: {
			title: 'Breakdown Cover Booklet',
			href: ACORN_BREAKDOWN_COVER_BOOKLET,
		},
		cv: {
			title: 'Breakdown Cover Booklet',
			href: ACORN_BREAKDOWN_COVER_BOOKLET,
		},
		tx: {
			title: 'Breakdown Cover Booklet',
			href: ACORN_BREAKDOWN_COVER_BOOKLET,
		},
		carrot: {
			title: 'Breakdown Cover Booklet',
			href: CARROT_BREAKDOWN_COVER_BOOKLET,
		},
		streetcover: {
			title: 'Breakdown Cover Booklet',
			href: STREETCOVER_BREAKDOWN_COVER_BOOKLET,
		},
	},
	{
		txFlag: {
			title: 'Breakdown Cover IPID',
			href: FLAG_BREAKDOWN_COVER_IPID,
		},
		pc: {
			title: 'Breakdown Cover IPID',
			href: ACORN_BREAKDOWN_COVER_IPID,
		},
		cv: {
			title: 'Breakdown Cover IPID',
			href: ACORN_BREAKDOWN_COVER_IPID,
		},
		tx: {
			title: 'Breakdown Cover IPID',
			href: ACORN_BREAKDOWN_COVER_IPID,
		},
		carrot: {
			title: 'Breakdown Cover IPID',
			href: CARROT_BREAKDOWN_COVER_IPID,
		},
		streetcover: {
			title: 'Breakdown Cover IPID',
			href: STREETCOVER_BREAKDOWN_COVER_IPID,
		},
	},
];

interface IProps {
	onSelect: (item: SelectedAddOn[]) => void;
	onRemove: (id: string) => void;
	option: AddOn[];
}

const BreakdownCoverTable: FC<IProps> = ({ onSelect, onRemove, option }) => {
	const renewalStore = useAppSelector((redux) => redux.renewal);
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const isStreetCover = import.meta.env.VITE_AUTH_REALM === 'StreetCover';

	const paymentInFull = renewalStore.paymentOptions.method === 'total';

	const breakdownEU = option?.find((i) => i.addOnCode === 'PC_BREAKDOWN_EU_UK');
	const breakdownNational = option?.find((i) => i.addOnCode === 'PC_BREAKDOWN_NATIONAL');

	const getCheapestOption = () => {
		if (breakdownEU && breakdownNational) {
			if (paymentInFull) {
				return breakdownEU.annualCost < breakdownNational.annualCost
					? breakdownEU.annualCost
					: breakdownNational.annualCost;
			}
			return breakdownEU.monthlyCost < breakdownNational.monthlyCost
				? breakdownEU.monthlyCost
				: breakdownNational.monthlyCost;
		}
		return 0;
	};

	const onAdd = (item: SelectedAddOn) => {
		onSelect([
			{
				underwrittenDataPointId: item.underwrittenDataPointId,
				addOnCode: item.addOnCode,
			},
		]);
	};

	const onHandleRemove = (underwrittenDataPointId: string) => {
		onRemove(underwrittenDataPointId);
	};

	return (
		<Box
			sx={(theme) => ({
				border: `2px solid ${theme.palette.grey[200]}`,
				borderRadius: 2,
			})}
		>
			<TableContainer
				sx={(theme) => ({
					'& td, th': {
						borderRight: `1px solid ${theme.palette.grey[200]}`,
						borderBottom: `1px solid${theme.palette.grey[200]}`,
						padding: '6px',
					},
					'& td:last-of-type, th:last-of-type': {
						borderRight: 'unset',
					},
					'& tfoot td': {
						borderBottom: 'unset',
						borderRight: 'unset',
					},
					color: theme.palette.common.black,
				})}
			>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell colSpan={COLUMNS.length}>
								<Typography sx={{ fontWeight: 'bold' }}>Breakdown Cover from</Typography>
								<Typography variant='h5' fontWeight='bold'>
									{formatCurrency(getCheapestOption())}
									{!paymentInFull && ' per month'}
								</Typography>
							</TableCell>
						</TableRow>
						<TableRow>
							{COLUMNS.map((column) => (
								<TableCell key={column.title}>{column.title}</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{BREAKDOWNCOVER.map((row) => (
							<TableRow key={`table-row-${row.name}`}>
								{COLUMNS.map((column) => {
									return (
										<TableCell align={column.align} key={`table-row-${column.field}`}>
											{GenerateValue(getKeyValue<IRowData, keyof IRowData>(row, column.field as keyof IRowData))}
										</TableCell>
									);
								})}
							</TableRow>
						))}
					</TableBody>
					<TableFooter>
						<TableRow>
							<TableCell />
							{renewalStore.paymentOptions.method && breakdownEU && breakdownNational && (
								<>
									<TableCell align='center'>
										{paymentInFull ? (
											<Typography fontWeight='bold'>{formatCurrency(breakdownNational.annualCost)}</Typography>
										) : (
											<Typography fontWeight='bold'>
												{formatCurrency(breakdownNational.monthlyCost)}
												PM
											</Typography>
										)}
										<Button
											aria-label={`${breakdownNational.selected ? 'remove' : 'add'}-breakdownNational`}
											variant='contained'
											disabled={breakdownEU.selected}
											onClick={() =>
												breakdownNational.selected
													? onHandleRemove(breakdownNational.underwrittenDataPointId)
													: onAdd(breakdownNational)
											}
										>
											{breakdownNational.selected ? 'Remove' : 'Add'}
										</Button>
									</TableCell>
									<TableCell align='center'>
										{paymentInFull ? (
											<Typography fontWeight='bold'>{formatCurrency(breakdownEU.annualCost)}</Typography>
										) : (
											<Typography fontWeight='bold'>
												{formatCurrency(breakdownEU.monthlyCost)}
												PM
											</Typography>
										)}
										<Button
											aria-label={`${breakdownNational.selected ? 'remove' : 'add'}-breakdownEu`}
											variant='contained'
											disabled={breakdownNational.selected}
											onClick={() =>
												breakdownEU.selected ? onHandleRemove(breakdownEU.underwrittenDataPointId) : onAdd(breakdownEU)
											}
										>
											{breakdownEU.selected ? 'Remove' : 'Add'}
										</Button>
									</TableCell>
								</>
							)}
						</TableRow>
						<TableRow>
							<TableCell colSpan={COLUMNS.length}>
								<Typography variant='body2' sx={{ mb: '1rem', fontStyle: 'italic' }}>
									{OPTIONAL_EXTRAS_MONTHLY_COST_DISCLAIMER}
								</Typography>
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell colSpan={COLUMNS.length}>
								<Accordion
									sx={(theme) => ({
										backgroundColor: 'unset',
										borderRadius: 'unset',
										borderTopLeftRadius: 'unset',
										borderTopRightRadius: 'unset',

										boxShadow: 'unset',
										borderBottom: `1px solid ${theme.palette.grey[300]}`,
										'&:before': {
											backgroundColor: 'unset',
										},
										'&:last-of-type': {
											borderBottomLeftRadius: 'unset',
											borderBottomRightRadius: 'unset',
										},
									})}
								>
									<AccordionSummary
										expandIcon={<ExpandMoreIcon />}
										aria-controls='tell-me-more-content'
										id='tell-me-more-header'
									>
										<Typography>Tell me more</Typography>
									</AccordionSummary>
									<AccordionDetails>
										<Typography>
											This product meets the demands and needs of someone who requires cover breakdown assistance in the
											UK, with an option for cover in Europe.
										</Typography>
										<br />
										<Typography>
											Key exclusions:
											<ul>
												<li>Roadside labour assistance in excess of one hour</li>
												<br />
												<li>More than 6 call-outs during any period of insurance</li>
											</ul>
										</Typography>
										<br />
										{BreakdownCoverLinks.map((link, index) => (
											<Typography key={`link-item-${index}`}>
												{isStreetCover && (
														<HyperLink href={link.streetcover.href} target='_blank' rel='noreferrer'>
															{link.streetcover.title}
														</HyperLink>
													)}
												{((fullPolicy.product === Product.PRIVATE_CAR && !isStreetCover) || fullPolicy.product === Product.TELEMATICS) && (
													<HyperLink href={link.pc.href} target='_blank' rel='noreferrer'>
														{link.pc.title}
													</HyperLink>
												)}
												{fullPolicy.product === Product.COMMERCIAL_VEHICLE && (
													<HyperLink href={link.cv.href} target='_blank' rel='noreferrer'>
														{link.cv.title}
													</HyperLink>
												)}
												{fullPolicy.product === Product.TAXI &&
													(import.meta.env.VITE_AUTH_REALM === 'Flag' ? (
														<HyperLink href={link.txFlag.href} target='_blank' rel='noreferrer'>
															{link.txFlag.title}
														</HyperLink>
													) : (
														<HyperLink href={link.tx.href} target='_blank' rel='noreferrer'>
															{link.tx.title}
														</HyperLink>
													))}
												{fullPolicy.product === Product.CARROT && (
													<HyperLink href={link.carrot.href} target='_blank' rel='noreferrer'>
														{link.carrot.title}
													</HyperLink>
												)}
											</Typography>
										))}
									</AccordionDetails>
								</Accordion>
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell colSpan={COLUMNS.length}>
								<Typography sx={(theme) => ({ color: theme.palette.grey[900] })}>
									Cover provided by Haven Insurance Company Limited Please refer to the Policy Booklet and Insurance
									Product Information Document for full terms and conditions.
								</Typography>
							</TableCell>
						</TableRow>
					</TableFooter>
				</Table>
			</TableContainer>
		</Box>
	);
};

export default BreakdownCoverTable;
